/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: International Trust
   Project: La Troupe
   Version: 5.5
   Status: LLCL-QA

   RESPONSIBILITY
   ----------------------------------------------------------
   Liquid/CSS owns the marquee, dimensions, icons, typography
   and layout.

   JavaScript owns:
   - collapsed / expanded state
   - HOME / non-PDP viewport motion band
   - PDP click-only interaction
   - accessibility
   - Shopify Theme Editor lifecycle
   - offscreen marquee pause / resume

   PDP RULE
   ----------------------------------------------------------
   Product routes are click-only. No scroll listener is bound,
   so the trust rail never auto-opens or auto-closes on PDP.
   ========================================================== */

(() => {
  'use strict';

  const VERSION = '5.5';
  const ROOT_SELECTOR = '[data-ll-trust]';
  const MANAGER_KEY = 'LLInternationalTrustManager';
  const MANUAL_RELEASE_DISTANCE = 120;

  const previousManager = window[MANAGER_KEY];

  if (previousManager && previousManager.version === VERSION) {
    previousManager.scan(document);
    return;
  }

  if (previousManager && typeof previousManager.destroy === 'function') {
    previousManager.destroy();
  }

  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const clamp = (value, minimum, maximum) =>
    Math.min(Math.max(value, minimum), maximum);

  const viewportHeight = () =>
    window.innerHeight || document.documentElement.clientHeight || 1;

  const isProductRoute = () =>
    /(^|\/)products\/[^/]+/.test(window.location.pathname || '');

  const resolveInteractionMode = root => {
    const explicitMode = root.dataset.interactionMode;

    if (explicitMode === 'click' || explicitMode === 'scroll') {
      return explicitMode;
    }

    return isProductRoute() ? 'click' : 'scroll';
  };

  class TrustController {
    constructor(root) {
      this.root = root;
      this.bar = root.querySelector('[data-ll-trust-bar]');
      this.drawer = root.querySelector('[data-ll-trust-drawer]');
      this.toggle = root.querySelector('[data-ll-trust-toggle]');
      this.toggleClosedLabel = root.querySelector(
        '.ll-trust-v5__toggle-closed'
      );
      this.toggleOpenLabel = root.querySelector(
        '.ll-trust-v5__toggle-open'
      );
      this.marqueeTrack = root.querySelector('.ll-trust-v5__marquee-track');

      this.destroyed = false;
      this.ready = Boolean(this.bar && this.drawer);
      this.expanded = false;
      this.blockEditing = false;
      this.manualOverride = false;
      this.manualStartY = window.scrollY;
      this.rafPending = false;
      this.resizeTimer = null;
      this.scrollListening = false;
      this.motionObserver = null;
      this.motionRangeActive = !('IntersectionObserver' in window);
      this.openLine = 68;
      this.closeLine = 14;
      this.previewLock = false;
      this.interactionMode = 'scroll';

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onToggle = this.onToggle.bind(this);

      if (!this.ready) return;

      this.readSettings();
      this.init();
    }

    readSettings() {
      this.interactionMode = resolveInteractionMode(this.root);

      this.openLine = clamp(
        toNumber(this.root.dataset.openLine, 68),
        40,
        95
      );

      this.closeLine = clamp(
        toNumber(this.root.dataset.closeLine, 14),
        0,
        45
      );

      if (this.closeLine >= this.openLine - 10) {
        this.closeLine = Math.max(5, this.openLine - 35);
      }

      const designMode = this.root.dataset.designMode === 'true';
      const previewState = this.root.dataset.previewState;

      this.previewLock = designMode && previewState === 'expanded';
      this.root.dataset.interactionModeResolved = this.interactionMode;
    }

    init() {
      this.root.classList.add('is-js-ready');
      this.root.classList.remove('is-preview-expanded');

      if (this.toggle) {
        this.toggle.addEventListener('click', this.onToggle);
      }

      window.addEventListener('resize', this.onResize, { passive: true });
      this.setAmbientMotionActive(false);
      this.initMotionRangeObserver();
      this.syncScrollBinding();

      this.setExpanded(this.previewLock, 'initial');

      window.requestAnimationFrame(() => {
        if (this.destroyed) return;

        if (this.interactionMode === 'scroll') {
          this.sync('initial');
        } else {
          this.syncAccessibility(this.expanded);
        }
      });
    }

    initMotionRangeObserver() {
      if (this.destroyed || !this.ready) return;

      const designMode = this.root.dataset.designMode === 'true';

      if (designMode || !('IntersectionObserver' in window)) {
        this.motionRangeActive = true;
        this.setAmbientMotionActive(true);
        return;
      }

      if (this.motionObserver) {
        this.motionObserver.disconnect();
      }

      this.motionRangeActive = false;
      this.setAmbientMotionActive(false);

      this.motionObserver = new IntersectionObserver(
        entries => {
          if (this.destroyed) return;

          const nextActive = entries.some(entry => entry.isIntersecting);

          if (nextActive === this.motionRangeActive) return;

          this.motionRangeActive = nextActive;
          this.manualOverride = false;
          this.setAmbientMotionActive(nextActive);
          this.syncScrollBinding();
          this.sync(nextActive ? 'motion-range-enter' : 'motion-range-leave');
        },
        {
          rootMargin: '200px 0px',
          threshold: 0
        }
      );

      this.motionObserver.observe(this.root);
    }

    syncScrollBinding() {
      const shouldListen =
        this.interactionMode === 'scroll' &&
        this.motionRangeActive;

      if (shouldListen && !this.scrollListening) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.scrollListening = true;
        return;
      }

      if (!shouldListen && this.scrollListening) {
        window.removeEventListener('scroll', this.onScroll);
        this.scrollListening = false;
      }
    }

    setAmbientMotionActive(active) {
      if (!this.marqueeTrack) return;

      const shouldRun = Boolean(active) && !this.expanded;

      this.marqueeTrack.style.animationPlayState = shouldRun
        ? 'running'
        : 'paused';
    }

    sync(source = 'geometry') {
      if (this.destroyed || !this.ready) return;

      if (this.previewLock) {
        this.setExpanded(true, 'preview-lock');
        return;
      }

      if (this.blockEditing) {
        this.setExpanded(true, 'block-edit');
        return;
      }

      if (this.interactionMode === 'click') {
        return;
      }

      if (this.manualOverride) {
        return;
      }

      const rect = this.bar.getBoundingClientRect();
      const vh = viewportHeight();
      const probe = rect.top + rect.height * 0.5;
      const openY = vh * (this.openLine / 100);
      const closeY = vh * (this.closeLine / 100);
      const insideBand = probe <= openY && probe > closeY;

      this.setExpanded(insideBand, source);
    }

    onScroll() {
      if (this.destroyed || this.interactionMode !== 'scroll') return;

      if (this.manualOverride) {
        const movement = Math.abs(window.scrollY - this.manualStartY);

        if (movement >= MANUAL_RELEASE_DISTANCE) {
          this.manualOverride = false;
        }
      }

      if (this.rafPending) return;

      this.rafPending = true;

      window.requestAnimationFrame(() => {
        this.rafPending = false;
        this.sync('scroll');
      });
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        if (this.destroyed) return;

        const previousMode = this.interactionMode;
        this.readSettings();
        this.syncScrollBinding();

        if (this.interactionMode === 'scroll') {
          this.manualOverride = false;
          this.sync('resize');
          return;
        }

        if (previousMode !== 'click' && !this.previewLock && !this.blockEditing) {
          this.setExpanded(false, 'mode-change');
        }
      }, 120);
    }

    setExpanded(open, source = 'unknown') {
      if (!this.ready) return;

      const next = Boolean(open);

      if (next === this.expanded) {
        this.syncAccessibility(next);
        this.setAmbientMotionActive(this.motionRangeActive);
        return;
      }

      if (
        !next &&
        document.activeElement &&
        this.drawer.contains(document.activeElement)
      ) {
        document.activeElement.blur();
      }

      this.expanded = next;
      this.root.classList.toggle('is-expanded', next);
      this.root.classList.toggle('is-collapsed', !next);
      this.root.dataset.motionState = next ? 'expanded' : 'collapsed';

      this.syncAccessibility(next);
      this.setAmbientMotionActive(this.motionRangeActive);
      this.dispatchStateEvent(source);
    }

    syncAccessibility(open) {
      if (!this.ready) return;

      this.drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      this.drawer.inert = !open;

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

        const activeLabel = (open ? this.toggleOpenLabel : this.toggleClosedLabel)
          ?.textContent
          ?.trim();

        if (activeLabel) {
          this.toggle.setAttribute('aria-label', activeLabel);
        }
      }

      if (this.toggleClosedLabel) {
        this.toggleClosedLabel.setAttribute('aria-hidden', open ? 'true' : 'false');
      }

      if (this.toggleOpenLabel) {
        this.toggleOpenLabel.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
    }

    onToggle(event) {
      event.preventDefault();

      if (this.previewLock) return;

      if (this.interactionMode === 'click') {
        this.manualOverride = false;
        this.setExpanded(!this.expanded, 'manual-click');
        return;
      }

      this.manualOverride = true;
      this.manualStartY = window.scrollY;
      this.setExpanded(!this.expanded, 'manual');
    }

    setBlockEditing(active) {
      this.blockEditing = Boolean(active);
      this.manualOverride = false;

      if (this.blockEditing) {
        this.setExpanded(true, 'block-select');
        return;
      }

      if (this.interactionMode === 'click') {
        if (this.previewLock) {
          this.setExpanded(true, 'preview-lock');
        } else {
          this.setExpanded(false, 'block-deselect');
        }
        return;
      }

      this.sync('block-deselect');
    }

    refresh(source = 'refresh') {
      if (this.destroyed || !this.ready) return;

      const previousMode = this.interactionMode;
      this.readSettings();
      this.syncScrollBinding();
      this.manualOverride = false;

      if (this.interactionMode === 'click') {
        if (this.previewLock || this.blockEditing) {
          this.setExpanded(true, source);
        } else if (previousMode !== 'click') {
          this.setExpanded(false, 'mode-change');
        } else {
          this.syncAccessibility(this.expanded);
          this.setAmbientMotionActive(this.motionRangeActive);
        }
        return;
      }

      this.sync(source);
    }

    dispatchStateEvent(source) {
      this.root.dispatchEvent(
        new CustomEvent('ll:trust-state', {
          bubbles: true,
          detail: {
            state: this.expanded ? 'expanded' : 'collapsed',
            source,
            interactionMode: this.interactionMode
          }
        })
      );
    }

    destroy() {
      if (this.destroyed) return;

      this.destroyed = true;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);

      if (this.motionObserver) {
        this.motionObserver.disconnect();
        this.motionObserver = null;
      }

      if (this.toggle) {
        this.toggle.removeEventListener('click', this.onToggle);
      }

      if (this.marqueeTrack) {
        this.marqueeTrack.style.removeProperty('animation-play-state');
      }

      this.scrollListening = false;
    }
  }

  const controllers = new Map();

  const prune = () => {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  };

  const initialize = root => {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new TrustController(root));
  };

  const scan = (context = document) => {
    prune();

    if (context.matches && context.matches(ROOT_SELECTOR)) {
      initialize(context);
    }

    if (context.querySelectorAll) {
      context.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
    }
  };

  const findRoot = target => {
    if (!target) return null;

    if (target.matches && target.matches(ROOT_SELECTOR)) {
      return target;
    }

    if (target.closest) {
      const closest = target.closest(ROOT_SELECTOR);
      if (closest) return closest;
    }

    if (target.querySelector) {
      return target.querySelector(ROOT_SELECTOR);
    }

    return null;
  };

  const controllerFor = target => {
    const root = findRoot(target);
    return root ? controllers.get(root) : null;
  };

  const destroyWithin = target => {
    controllers.forEach((controller, root) => {
      const belongs =
        root === target ||
        (target && typeof target.contains === 'function' && target.contains(root));

      if (!belongs) return;

      controller.destroy();
      controllers.delete(root);
    });
  };

  const onSectionLoad = event => {
    scan(event.target);

    window.requestAnimationFrame(() => {
      const controller = controllerFor(event.target);
      if (controller) controller.refresh('section-load');
    });
  };

  const onSectionUnload = event => destroyWithin(event.target);

  const onSectionSelect = event => {
    const controller = controllerFor(event.target);
    if (controller) controller.refresh('section-select');
  };

  const onSectionDeselect = event => {
    const controller = controllerFor(event.target);
    if (controller) controller.refresh('section-deselect');
  };

  const onBlockSelect = event => {
    const controller = controllerFor(event.target);
    if (controller) controller.setBlockEditing(true);
  };

  const onBlockDeselect = event => {
    const controller = controllerFor(event.target);
    if (controller) controller.setBlockEditing(false);
  };

  const onSectionReorder = () => {
    window.requestAnimationFrame(() => {
      controllers.forEach(controller => controller.refresh('section-reorder'));
    });
  };

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:select', onSectionSelect);
  document.addEventListener('shopify:section:deselect', onSectionDeselect);
  document.addEventListener('shopify:block:select', onBlockSelect);
  document.addEventListener('shopify:block:deselect', onBlockDeselect);
  document.addEventListener('shopify:section:reorder', onSectionReorder);

  const manager = {
    version: VERSION,
    scan,

    refresh() {
      scan(document);
      controllers.forEach(controller => controller.refresh('manager-refresh'));
    },

    destroy() {
      controllers.forEach(controller => controller.destroy());
      controllers.clear();

      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      document.removeEventListener('shopify:section:deselect', onSectionDeselect);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      document.removeEventListener('shopify:section:reorder', onSectionReorder);
    },

    open() {
      controllers.forEach(controller => {
        controller.previewLock = false;
        controller.manualOverride = controller.interactionMode === 'scroll';
        controller.manualStartY = window.scrollY;
        controller.setExpanded(true, 'manager-open');
      });
    },

    close() {
      controllers.forEach(controller => {
        controller.previewLock = false;
        controller.manualOverride = controller.interactionMode === 'scroll';
        controller.manualStartY = window.scrollY;
        controller.setExpanded(false, 'manager-close');
      });
    }
  };

  window[MANAGER_KEY] = manager;
  window.LLInternationalTrustMotion = manager;
  window.LLInternationalTrust = manager;

  const start = () => scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
